import { Link, useLocation } from 'react-router';
import cs from 'classnames';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { DeviceType } from '@repo/types';
import { useRef, useLayoutEffect, useMemo, useState, useEffect } from 'react';

type NavigationProps = {
  className?: string;
};

type ParsedUrl = {
  path: string;
  query?: string;
  hash?: string;
};

// Helper function to parse URL into components
const parseUrl = (url: string): ParsedUrl => {
  // Handle URLs that are just a hash (e.g., #midlands)
  if (url.startsWith('#')) {
    return { path: '', query: undefined, hash: url };
  }

  const [pathWithQuery = '', hash] = url.split('#');
  const [path = '', query] = pathWithQuery.split('?');
  // Ensure hash includes the # prefix if it exists
  return { path, query, hash: hash ? `#${hash}` : undefined };
};

// Check if URL is external
const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http') || (url.includes('.') && !url.startsWith('/'));
};

// Normalize path by ensuring it starts with /
const normalizePath = (path: string): string => {
  if (!path) return '';
  return path.startsWith('/') ? path : `/${path}`;
};

// Check if two URLs match based on their components
const urlsMatch = (navUrl: ParsedUrl, currentUrl: ParsedUrl, navUrlString: string, currentUrlString: string): boolean => {
  // Normalize paths for comparison
  const normalizedNavPath = normalizePath(navUrl.path);
  const normalizedCurrentPath = normalizePath(currentUrl.path);

  // Exact match (full string comparison) - also try with normalized paths
  if (navUrlString === currentUrlString) {
    return true;
  }

  // Match with hash (e.g., /club-overview#premier)
  // Hashes are already normalized with # prefix in parseUrl
  if (navUrl.hash && currentUrl.hash) {
    // If nav item is just a hash (e.g., #midlands), match any path with that hash
    if (!navUrl.path && navUrl.hash === currentUrl.hash) {
      return true;
    }
    // If both have paths, they must match along with the hash
    if (normalizedNavPath === normalizedCurrentPath && navUrl.hash === currentUrl.hash) {
      return true;
    }
  }

  // Also check path match without hash if nav doesn't have hash but current does
  if (!navUrl.hash && !navUrl.query && currentUrl.path) {
    if (normalizedNavPath === normalizedCurrentPath) {
      return true;
    }
  }

  return false;
};

// Check if a plain path should be active (fallback when no exact match exists)
const isPathActiveAsFallback = (
  navUrl: ParsedUrl,
  _currentUrl: ParsedUrl,
  currentPath: string,
  hasExactMatch: boolean
): boolean => {
  // Only match plain paths when nav item has no query/hash and no exact match exists
  if (!navUrl.query && !navUrl.hash && !hasExactMatch) {
    const normalizedNavPath = normalizePath(navUrl.path);
    const normalizedCurrentPath = normalizePath(currentPath);

    // Exact path match
    if (normalizedCurrentPath === normalizedNavPath) {
      return true;
    }
    // Nested route match
    if (normalizedNavPath !== '/' && normalizedCurrentPath.startsWith(normalizedNavPath + '/')) {
      return true;
    }
  }
  return false;
};

function Nav({ className }: NavigationProps) {
  // All hooks must come first, before any conditional logic
  const { data, isLoading, error } = useNavigationsQuery();
  const deviceType = useDeviceType();
  const location = useLocation();
  const navListRef = useRef<HTMLUListElement>(null);

  // Track hash changes manually since React Router doesn't always update location.hash
  const [currentHash, setCurrentHash] = useState(() => window.location.hash);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);

    // Also update when location changes
    setCurrentHash(window.location.hash);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location]);

  const isMobile = useMemo(() => deviceType === DeviceType.mobile || deviceType === DeviceType.tablet, [deviceType]);

  const headerNavigation = data?.header;
  const navigationNodes = useMemo(
    () => (isMobile ? headerNavigation?.mobileNodes : headerNavigation?.desktopNodes),
    [isMobile, headerNavigation]
  );

  // Ensure active item is visible in viewport on mobile
  useLayoutEffect(() => {
    if (!isMobile || !navListRef.current) return;

    const container = navListRef.current;

    // Scroll active item into view if it's not fully visible
    const ensureActiveItemVisible = () => {
      const activeLink = container.querySelector('.nav__link.active') as HTMLElement;
      if (!activeLink) return;

      const listItem = activeLink.closest('.nav__list-item') as HTMLElement;
      if (!listItem) return;

      const containerRect = container.getBoundingClientRect();
      const itemRect = listItem.getBoundingClientRect();

      // Check if item is fully visible
      const isFullyVisible =
        itemRect.left >= containerRect.left &&
        itemRect.right <= containerRect.right;

      if (!isFullyVisible) {
        // Calculate minimal scroll to make item visible
        let scrollPosition = container.scrollLeft;

        if (itemRect.left < containerRect.left) {
          // Item is to the left, scroll left
          scrollPosition += itemRect.left - containerRect.left - 10; // 10px padding
        } else if (itemRect.right > containerRect.right) {
          // Item is to the right, scroll right
          scrollPosition += itemRect.right - containerRect.right + 10; // 10px padding
        }

        container.scrollTo({
          left: scrollPosition,
          behavior: container.dataset.initialized === 'true' ? 'smooth' : 'instant',
        });
      }

      container.dataset.initialized = 'true';
    };

    // Ensure active item is visible on mount and when navigation changes
    ensureActiveItemVisible();

    // Use RAF for a second attempt to handle async rendering
    const rafId = requestAnimationFrame(ensureActiveItemVisible);

    return () => {
      cancelAnimationFrame(rafId);
      delete container.dataset.initialized;
    };
  }, [isMobile, location.pathname, location.search, navigationNodes]);

  // Memoize the navigation items to prevent unnecessary re-renders
  const navItems = useMemo(() => {
    if (!navigationNodes || navigationNodes.length === 0) {
      return [];
    }

    // Collect all navigation URLs for exact match checking
    const allNavUrls: string[] = [];
    navigationNodes.forEach(item => {
      if (item.url && !isExternalUrl(item.url)) {
        allNavUrls.push(item.url);
      }
      item.children?.forEach(child => {
        if (child.url && !isExternalUrl(child.url)) {
          allNavUrls.push(child.url);
        }
      });
    });

    // Current location - use tracked hash instead of location.hash
    const currentFullPath = location.pathname + location.search + currentHash;
    const currentParsed = parseUrl(currentFullPath);

    // Check if there's an exact match in the navigation
    const hasExactMatch = allNavUrls.some(url => {
      const urlParsed = parseUrl(url);
      return urlsMatch(urlParsed, currentParsed, url, currentFullPath);
    });

    // Helper to determine if a nav item is active
    const checkItemActive = (url: string | null | undefined): boolean => {
      if (!url || isExternalUrl(url)) return false;

      const itemParsed = parseUrl(url);

      // Check exact or hash match
      if (urlsMatch(itemParsed, currentParsed, url, currentFullPath)) {
        return true;
      }

      // Special case: If current URL has a hash but nav item is just the path
      // Mark the path as active (e.g., /club-overview is active when on /club-overview#midlands)
      if (currentParsed.hash && !itemParsed.hash && !itemParsed.query) {
        const normalizedItemPath = normalizePath(itemParsed.path);
        const normalizedCurrentPath = normalizePath(currentParsed.path);

        if (normalizedItemPath === normalizedCurrentPath) {
          // Only if there's no more specific match with the hash
          const hasSpecificHashMatch = allNavUrls.some(navUrl => {
            const parsed = parseUrl(navUrl);
            const normalizedNavPath = normalizePath(parsed.path);
            return normalizedNavPath === normalizedCurrentPath && parsed.hash === currentParsed.hash;
          });
          if (!hasSpecificHashMatch) {
            return true;
          }
        }
      }

      // Check fallback path match
      return isPathActiveAsFallback(itemParsed, currentParsed, location.pathname, hasExactMatch);
    };

    // Calculate match scores for each navigation item
    const itemsWithScores = navigationNodes.map((item) => {
      let bestScore = 0;
      let childIsActive = false;
      let activeChild = null;

      // Check children first for exact matches
      if (item.children) {
        for (const child of item.children) {
          if (checkItemActive(child.url)) {
            childIsActive = true;
            activeChild = child;
            bestScore = 100; // Child match gets highest priority
            break;
          }
        }
      }

      // Check the item itself if no child was active
      if (!childIsActive && item.url && !isExternalUrl(item.url)) {
        const itemParsed = parseUrl(item.url);
        const normalizedItemPath = normalizePath(itemParsed.path);
        const normalizedCurrentPath = normalizePath(currentParsed.path);

        // Exact full match (path + query + hash)
        if (item.url === currentFullPath) {
          bestScore = 90;
        }
        // Path + query match (for URLs like /news?tags=podcasts)
        else if (itemParsed.query && currentParsed.query &&
                 normalizedItemPath === normalizedCurrentPath &&
                 itemParsed.query === currentParsed.query) {
          bestScore = 80;
        }
        // Path + hash match
        else if (itemParsed.hash && currentParsed.hash &&
                 normalizedItemPath === normalizedCurrentPath &&
                 itemParsed.hash === currentParsed.hash) {
          bestScore = 70;
        }
        // Path only match
        else if (!itemParsed.query && !itemParsed.hash &&
                 normalizedItemPath === normalizedCurrentPath) {
          bestScore = 50;
        }
        // Partial path match (nested routes)
        else if (!itemParsed.query && !itemParsed.hash &&
                 normalizedItemPath !== '/' &&
                 normalizedCurrentPath.startsWith(normalizedItemPath + '/')) {
          bestScore = 30;
        }
      }

      return {
        item,
        score: bestScore,
        childIsActive,
        activeChild
      };
    });

    // Find the best matching root item
    const bestRootMatch = itemsWithScores.reduce<{
      item: typeof navigationNodes[0] | null;
      score: number;
      childIsActive: boolean;
      activeChild: any;
    }>((best, current) => {
      if (current.score > best.score) {
        return current;
      }
      return best;
    }, { item: null, score: 0, childIsActive: false, activeChild: null });

    // Process navigation items with the scoring results
    return navigationNodes.map((item) => {
      const scoreItem = itemsWithScores.find(si => si.item === item);
      const isTheBestMatch = bestRootMatch.item === item && bestRootMatch.score > 0;

      return {
        ...item,
        isActive: isTheBestMatch,
        children: item.children?.map((child) => ({
          ...child,
          isActive: isTheBestMatch && scoreItem?.activeChild === child,
        })),
      };
    });
  }, [navigationNodes, location.pathname, location.search, currentHash]);

  // Early returns come after all hooks
  if (isLoading) {
    return null;
  }

  if (error || !navigationNodes || navigationNodes.length === 0) {
    return null;
  }

  return (
    <nav className={cs('nav', className)}>
      <ul className="nav__list" ref={navListRef}>
        {navItems.map((item) => (
          <li className={cs('nav__list-item', { 'active-parent': item.isActive })} key={item.label}>
            {item.url ? (
              <Link
                className={cs('nav__link standalone', { active: item.isActive })}
                to={item.url.includes('.') && !item.url.startsWith('http') ? `https://${item.url}` : item.url}
                target={item.sameTab ? '_self' : '_blank'}
              >
                {item.label}
              </Link>
            ) : (
              item.label
            )}
            {item.children && item.children.length > 0 && (
              <ul>
                {item.children.map((child) => (
                  <li key={child.id}>
                    {child.url ? (
                      <Link
                        className={cs('nav__link standalone', { active: child.isActive })}
                        to={
                          child.url.includes('.') && !child.url.startsWith('http') ? `https://${child.url}` : child.url
                        }
                        target={item.sameTab ? '_self' : '_blank'}
                      >
                        {child.label}
                      </Link>
                    ) : (
                      child.label
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default Nav;
